import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { Eye, EyeOff, RefreshCw, Calendar, MapPin, Users, CreditCard, Phone, Mail, CheckCircle, Clock, XCircle, Share2, Trash2, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingsAdmin() {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, paid
  const [viewMode, setViewMode] = useState('list'); // list or calendar
  const [shareMenuBooking, setShareMenuBooking] = useState(null); // Track which booking's share menu is open
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareMenuBooking && !event.target.closest('.share-menu-container')) {
        setShareMenuBooking(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [shareMenuBooking]);

  // Handle PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handler);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      // Already installed
    }
    
    // Check notification permission
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallApp = async () => {
    if (!installPrompt) {
      toast.info('App is already installed or not available for installation');
      return;
    }
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('App installed successfully!');
      setInstallPrompt(null);
    }
  };

  const handleEnableNotifications = async () => {
    if (!('Notification' in window)) {
      toast.error('Notifications not supported on this device');
      return;
    }
    
    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        toast.success('Notifications enabled! You\'ll receive alerts for new bookings');
        
        // Subscribe to push notifications
        const registration = await navigator.serviceWorker.ready;
        // In production, you would subscribe to a push service here
        console.log('Push notification subscription ready');
      } else {
        toast.error('Notification permission denied');
      }
    } catch (error) {
      console.error('Notification error:', error);
      toast.error('Failed to enable notifications');
    }
  };

  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'santorini2024';

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      fetchBookings();
    } else {
      alert('Wrong password!');
    }
  };

  const handleShareBooking = (booking, method) => {
    const bookingText = `
🎫 Booking Details:
━━━━━━━━━━━━━━━━━━━
📋 ID: ${booking.booking_id}
👤 Customer: ${booking.customer_name}
📧 Email: ${booking.customer_email}
📞 Phone: ${booking.customer_phone || 'N/A'}

🚗 Vehicle: ${booking.vehicle_name}
👥 Passengers: ${booking.passengers}
📅 Date: ${new Date(booking.pickup_datetime).toLocaleString()}

📍 Pickup: ${booking.pickup_location}
${booking.destination ? `📍 Destination: ${booking.destination}` : ''}

💰 Total Fare: €${booking.fare.toFixed(2)}
${booking.payment_type === 'full' 
  ? `✅ Full Payment: €${booking.fare.toFixed(2)}`
  : `💵 Deposit: €${(booking.deposit_amount || booking.fare * 0.1).toFixed(2)} | Cash: €${(booking.fare - (booking.deposit_amount || booking.fare * 0.1)).toFixed(2)}`
}

💳 Payment Status: ${booking.payment_status === 'paid' ? '✅ Paid' : '⏳ Pending'}
${booking.notes ? `\n📝 Notes: ${booking.notes}` : ''}
    `.trim();

    const encodedText = encodeURIComponent(bookingText);
    
    if (method === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodedText}`, '_blank');
      setShareMenuBooking(null);
    } else if (method === 'viber') {
      window.open(`viber://forward?text=${encodedText}`, '_blank');
      setShareMenuBooking(null);
    } else if (method === 'email') {
      const subject = encodeURIComponent(`Booking ${booking.booking_id} - ${booking.customer_name}`);
      window.open(`mailto:?subject=${subject}&body=${encodedText}`, '_blank');
      setShareMenuBooking(null);
    } else if (method === 'sms') {
      window.open(`sms:?body=${encodedText}`, '_blank');
      setShareMenuBooking(null);
    } else {
      // Copy to clipboard
      navigator.clipboard.writeText(bookingText);
      toast.success('Booking details copied to clipboard!');
      setShareMenuBooking(null);
    }
  };

  const toggleShareMenu = (e, bookingId) => {
    e.stopPropagation();
    setShareMenuBooking(shareMenuBooking === bookingId ? null : bookingId);
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/bookings/${bookingId}/cancel`);
      toast.success('Booking cancelled successfully!');
      fetchBookings(); // Refresh list
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      toast.error('Failed to cancel booking. Please try again.');
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings/all-public`);
      // Sort by created_at desc (newest first)
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setBookings(sorted);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'pending') return booking.payment_status === 'pending';
    if (filter === 'paid') return booking.payment_status === 'paid';
    return true;
  });

  // Group bookings by date for calendar view
  const bookingsByDate = {};
  filteredBookings.forEach(booking => {
    const date = new Date(booking.pickup_datetime).toISOString().split('T')[0];
    if (!bookingsByDate[date]) {
      bookingsByDate[date] = [];
    }
    bookingsByDate[date].push(booking);
  });

  // Get sorted dates
  const sortedDates = Object.keys(bookingsByDate).sort();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex items-center justify-center p-4">
        <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold text-white mb-6 text-center">Admin Access</h1>
          <div className="space-y-4">
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="bg-zinc-900 border-zinc-700 text-white pr-12"
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <Button onClick={handleLogin} className="w-full bg-amber-500 hover:bg-amber-600">
              Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 py-4 sm:py-8 px-3 sm:px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Bookings Dashboard</h1>
            <p className="text-zinc-400 text-sm sm:text-base">Manage all your bookings</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {installPrompt && (
              <Button 
                onClick={handleInstallApp}
                size="sm"
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                📱 Install App
              </Button>
            )}
            <Button 
              onClick={notificationsEnabled ? null : handleEnableNotifications}
              disabled={notificationsEnabled}
              size="sm"
              className={notificationsEnabled ? 'bg-zinc-700 text-zinc-400' : 'bg-blue-500 hover:bg-blue-600'}
            >
              {notificationsEnabled ? <BellOff className="w-4 h-4 mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
              {notificationsEnabled ? 'Notifications On' : 'Enable Alerts'}
            </Button>
            <Button 
              onClick={fetchBookings} 
              disabled={loading}
              size="sm"
              className="bg-amber-500 hover:bg-amber-600"
            >
              <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button
              onClick={() => setFilter('all')}
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              className={filter === 'all' ? 'bg-amber-500' : 'border-zinc-700'}
            >
              All ({bookings.length})
            </Button>
            <Button
              onClick={() => setFilter('pending')}
              variant={filter === 'pending' ? 'default' : 'outline'}
              size="sm"
              className={filter === 'pending' ? 'bg-amber-500' : 'border-zinc-700'}
            >
              <Clock className="w-4 h-4 mr-1 sm:mr-2" />
              Pending ({bookings.filter(b => b.payment_status === 'pending').length})
            </Button>
            <Button
              onClick={() => setFilter('paid')}
              variant={filter === 'paid' ? 'default' : 'outline'}
              size="sm"
              className={filter === 'paid' ? 'bg-green-500' : 'border-zinc-700'}
            >
              <CheckCircle className="w-4 h-4 mr-1 sm:mr-2" />
              Paid ({bookings.filter(b => b.payment_status === 'paid').length})
            </Button>
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex gap-2 bg-zinc-800 rounded-lg p-1">
            <Button
              onClick={() => setViewMode('list')}
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className={viewMode === 'list' ? 'bg-amber-500' : ''}
            >
              List
            </Button>
            <Button
              onClick={() => setViewMode('calendar')}
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              className={viewMode === 'calendar' ? 'bg-amber-500' : ''}
            >
              <Calendar className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Calendar</span>
              <span className="sm:hidden">Cal</span>
            </Button>
          </div>
        </div>

        {/* Bookings List or Calendar */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-amber-500 mx-auto" />
          </div>
        ) : viewMode === 'calendar' ? (
          /* Calendar View */
          <div className="space-y-4 sm:space-y-6">
            {sortedDates.map((date) => (
              <div key={date} className="bg-zinc-800 border border-zinc-700 rounded-xl p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 flex-wrap">
                  <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
                  <h2 className="text-lg sm:text-2xl font-bold text-white">
                    {new Date(date).toLocaleDateString('en-GB', { 
                      weekday: window.innerWidth < 640 ? 'short' : 'long',
                      year: 'numeric', 
                      month: window.innerWidth < 640 ? 'short' : 'long',
                      day: 'numeric' 
                    })}
                  </h2>
                  <span className="px-2 sm:px-3 py-1 bg-amber-500/20 text-amber-400 rounded-lg text-xs sm:text-sm font-semibold">
                    {bookingsByDate[date].length} booking{bookingsByDate[date].length !== 1 ? 's' : ''}
                  </span>
                </div>
                
                <div className="space-y-2 sm:space-y-3">
                  {bookingsByDate[date].map((booking) => (
                    <div key={booking.booking_id} className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 sm:p-4 hover:border-amber-500/50 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                            <span className="text-white font-semibold text-sm sm:text-base">
                              {new Date(booking.pickup_datetime).toLocaleTimeString('en-GB', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </span>
                            <span className="text-zinc-500 hidden sm:inline">•</span>
                            <span className="text-amber-400 font-semibold text-sm sm:text-base truncate">{booking.vehicle_name}</span>
                            <span className="text-zinc-500 hidden sm:inline">•</span>
                            <span className="text-zinc-300 text-sm sm:text-base truncate">{booking.customer_name}</span>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-zinc-400">
                            <div className="flex items-center gap-1 min-w-0">
                              <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                              <span className="truncate">{booking.pickup_location}</span>
                            </div>
                            {booking.destination && (
                              <>
                                <span className="hidden sm:inline">→</span>
                                <span className="truncate">{booking.destination}</span>
                              </>
                            )}
                            <div className="flex items-center gap-1">
                              <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span>{booking.passengers}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <a href={`tel:${booking.customer_phone}`} className="text-xs text-amber-400 hover:text-amber-300 truncate">
                              {booking.customer_phone}
                            </a>
                            <span className="text-zinc-600">•</span>
                            <a href={`mailto:${booking.customer_email}`} className="text-xs text-amber-400 hover:text-amber-300 truncate">
                              {booking.customer_email}
                            </a>
                          </div>
                        </div>
                        
                        <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 justify-between sm:justify-start">
                          {booking.payment_status === 'paid' ? (
                            <span className="px-2 sm:px-3 py-1 bg-green-500/20 text-green-400 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" /> Paid
                            </span>
                          ) : (
                            <span className="px-2 sm:px-3 py-1 bg-amber-500/20 text-amber-400 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-1">
                              <Clock className="w-3 h-3 sm:w-4 sm:h-4" /> Pending
                            </span>
                          )}
                          {booking.payment_type === 'full' ? (
                            <span className="text-green-400 font-bold text-sm sm:text-base">€{booking.fare.toFixed(2)}</span>
                          ) : (
                            <div className="text-right">
                              <div className="text-amber-400 font-bold text-sm sm:text-base">€{(booking.deposit_amount || booking.fare * 0.1).toFixed(2)}</div>
                              <div className="text-xs text-zinc-500">+€{(booking.fare - (booking.deposit_amount || booking.fare * 0.1)).toFixed(2)} cash</div>
                            </div>
                          )}
                          
                          {/* Action Buttons */}
                          <div className="flex gap-2 sm:mt-2">
                            <div className="relative share-menu-container">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => toggleShareMenu(e, booking.booking_id)}
                                className="border-zinc-700 hover:bg-blue-500/10 hover:border-blue-500 h-8 w-8 sm:h-9 sm:w-9 p-0"
                              >
                                <Share2 className="w-3 h-3 sm:w-4 sm:h-4" />
                              </Button>
                              
                              {/* Share Dropdown Menu */}
                              {shareMenuBooking === booking.booking_id && (
                                <div className="absolute right-0 top-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-2xl z-[100] min-w-[160px] overflow-hidden">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShareBooking(booking, 'whatsapp');
                                    }}
                                    className="w-full px-4 py-3 text-left text-white hover:bg-green-500/20 flex items-center gap-2 text-sm transition-colors"
                                  >
                                    <span className="text-lg">📱</span>
                                    <span>WhatsApp</span>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShareBooking(booking, 'viber');
                                    }}
                                    className="w-full px-4 py-3 text-left text-white hover:bg-purple-500/20 flex items-center gap-2 text-sm transition-colors"
                                  >
                                    <span className="text-lg">💬</span>
                                    <span>Viber</span>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShareBooking(booking, 'email');
                                    }}
                                    className="w-full px-4 py-3 text-left text-white hover:bg-blue-500/20 flex items-center gap-2 text-sm transition-colors"
                                  >
                                    <span className="text-lg">📧</span>
                                    <span>Email</span>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShareBooking(booking, 'sms');
                                    }}
                                    className="w-full px-4 py-3 text-left text-white hover:bg-amber-500/20 flex items-center gap-2 text-sm transition-colors"
                                  >
                                    <span className="text-lg">💬</span>
                                    <span>SMS</span>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShareBooking(booking, 'copy');
                                    }}
                                    className="w-full px-4 py-3 text-left text-white hover:bg-zinc-700 flex items-center gap-2 text-sm border-t border-zinc-700 transition-colors"
                                  >
                                    <span className="text-lg">📋</span>
                                    <span>Copy</span>
                                  </button>
                                </div>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCancelBooking(booking.booking_id)}
                              className="border-zinc-700 hover:bg-red-500/10 hover:border-red-500 h-8 w-8 sm:h-9 sm:w-9 p-0"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            {sortedDates.length === 0 && (
              <div className="text-center py-12 text-zinc-500">
                No bookings found
              </div>
            )}
          </div>
        ) : (
          /* List View */
          <div className="space-y-3 sm:space-y-4">
            {filteredBookings.map((booking) => (
              <div key={booking.booking_id} className="bg-zinc-800 border border-zinc-700 rounded-xl p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-4">
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-1">
                      {booking.vehicle_name} - {booking.booking_type}
                    </h3>
                    <p className="text-zinc-500 text-xs sm:text-sm">ID: {booking.booking_id}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {booking.payment_status === 'paid' ? (
                      <span className="px-2 sm:px-3 py-1 bg-green-500/20 text-green-400 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" /> Paid
                      </span>
                    ) : (
                      <span className="px-2 sm:px-3 py-1 bg-amber-500/20 text-amber-400 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-1">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4" /> Pending
                      </span>
                    )}
                    {booking.payment_type === 'full' && (
                      <span className="px-2 sm:px-3 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-xs sm:text-sm font-semibold">
                        Full Payment
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                  {/* Customer Info */}
                  <div className="space-y-2">
                    <h4 className="text-white font-semibold mb-2 text-sm sm:text-base">Customer Info</h4>
                    <div className="flex items-center gap-2 text-zinc-300 text-sm">
                      <Users className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500 flex-shrink-0" />
                      <span className="truncate">{booking.customer_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-300 text-sm">
                      <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500 flex-shrink-0" />
                      <a href={`mailto:${booking.customer_email}`} className="hover:text-amber-400 truncate">
                        {booking.customer_email}
                      </a>
                    </div>
                    {booking.customer_phone && (
                      <div className="flex items-center gap-2 text-zinc-300 text-sm">
                        <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500 flex-shrink-0" />
                        <a href={`tel:${booking.customer_phone}`} className="hover:text-amber-400">
                          {booking.customer_phone}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Trip Info */}
                  <div className="space-y-2">
                    <h4 className="text-white font-semibold mb-2 text-sm sm:text-base">Trip Details</h4>
                    <div className="flex items-start gap-2 text-zinc-300 text-sm">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                      <span className="break-words">{new Date(booking.pickup_datetime).toLocaleString()}</span>
                    </div>
                    <div className="flex items-start gap-2 text-zinc-300 text-sm">
                      <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                      <span className="break-words">{booking.pickup_location}</span>
                    </div>
                    {booking.destination && (
                      <div className="flex items-start gap-2 text-zinc-300 text-sm">
                        <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                        <span className="break-words">→ {booking.destination}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-zinc-300 text-sm">
                      <Users className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500 flex-shrink-0" />
                      <span>{booking.passengers} passengers</span>
                    </div>
                  </div>
                </div>

                {/* Payment Info */}
                <div className="bg-zinc-900 rounded-lg p-3 sm:p-4 border border-zinc-700">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div>
                      <p className="text-zinc-400 text-xs sm:text-sm">Total Fare</p>
                      <p className="text-white font-bold text-lg sm:text-xl">€{booking.fare.toFixed(2)}</p>
                    </div>
                    {booking.payment_type === 'full' ? (
                      <div className="text-left sm:text-right">
                        <p className="text-green-400 text-xs sm:text-sm">Full Payment</p>
                        <p className="text-green-400 font-bold text-lg sm:text-xl">€{booking.fare.toFixed(2)}</p>
                      </div>
                    ) : (
                      <div className="flex gap-4 sm:gap-0 sm:flex-row">
                        <div className="text-left sm:text-right">
                          <p className="text-amber-400 text-xs sm:text-sm">Deposit (10%)</p>
                          <p className="text-amber-400 font-bold text-lg sm:text-xl">
                            €{(booking.deposit_amount || booking.fare * 0.1).toFixed(2)}
                          </p>
                        </div>
                        <div className="text-left sm:text-right sm:ml-4">
                          <p className="text-zinc-400 text-xs sm:text-sm">Remaining</p>
                          <p className="text-white font-bold text-lg sm:text-xl">
                            €{(booking.fare - (booking.deposit_amount || booking.fare * 0.1)).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {booking.notes && (
                  <div className="mt-4 bg-zinc-900 rounded-lg p-3 border border-zinc-700">
                    <p className="text-zinc-400 text-xs sm:text-sm">Notes:</p>
                    <p className="text-white text-sm sm:text-base">{booking.notes}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-4 pt-4 border-t border-zinc-700 gap-3 sm:gap-0">
                  <div className="text-xs text-zinc-500">
                    Created: {new Date(booking.created_at).toLocaleString()}
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="relative share-menu-container">
                      <Button
                        size="sm"
                        onClick={(e) => toggleShareMenu(e, booking.booking_id)}
                        className="bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/30"
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                      </Button>
                      
                      {/* Share Dropdown Menu */}
                      {shareMenuBooking === booking.booking_id && (
                        <div className="absolute right-0 bottom-full mb-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-2xl z-[100] min-w-[160px] overflow-hidden">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareBooking(booking, 'whatsapp');
                            }}
                            className="w-full px-4 py-3 text-left text-white hover:bg-green-500/20 flex items-center gap-2 text-sm rounded-t-lg transition-colors"
                          >
                            <span className="text-lg">📱</span>
                            <span>WhatsApp</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareBooking(booking, 'viber');
                            }}
                            className="w-full px-4 py-3 text-left text-white hover:bg-purple-500/20 flex items-center gap-2 text-sm transition-colors"
                          >
                            <span className="text-lg">💬</span>
                            <span>Viber</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareBooking(booking, 'email');
                            }}
                            className="w-full px-4 py-3 text-left text-white hover:bg-blue-500/20 flex items-center gap-2 text-sm transition-colors"
                          >
                            <span className="text-lg">📧</span>
                            <span>Email</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareBooking(booking, 'sms');
                            }}
                            className="w-full px-4 py-3 text-left text-white hover:bg-amber-500/20 flex items-center gap-2 text-sm transition-colors"
                          >
                            <span className="text-lg">💬</span>
                            <span>SMS</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareBooking(booking, 'copy');
                            }}
                            className="w-full px-4 py-3 text-left text-white hover:bg-zinc-700 flex items-center gap-2 text-sm border-t border-zinc-700 rounded-b-lg transition-colors"
                          >
                            <span className="text-lg">📋</span>
                            <span>Copy</span>
                          </button>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleCancelBooking(booking.booking_id)}
                      className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {filteredBookings.length === 0 && (
              <div className="text-center py-12 text-zinc-500">
                No bookings found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
